/**
 * Mystery box helper functions
 */

import type { MysteryType, DealProduct } from '../types';
import { MYSTERY_TIERS, RUMIO_CONFIG } from './constants';

/**
 * Get mystery tier configuration
 */
export function getMysteryTierConfig(type: MysteryType) {
  return MYSTERY_TIERS[type];
}

/**
 * Get Rumio configuration
 */
export function getRumioConfig(type: MysteryType) {
  return RUMIO_CONFIG[type];
}

/**
 * Get CSS classes for mystery box card
 */
export function getMysteryBoxClasses(type: MysteryType): string {
  const config = MYSTERY_TIERS[type];
  return `${config.bgColor} ${config.borderColor}`;
}

/**
 * Get glow animation class based on mystery type
 */
export function getMysteryGlowClass(type: MysteryType): string {
  switch (type) {
    case 'super':
      return 'animate-pulse-gold';
    case 'fresh':
      return 'animate-pulse-purple';
    case 'standard':
      return 'animate-pulse-blue';
  }
}

/**
 * Get mystery box icon
 */
export function getMysteryIcon(type: MysteryType): string {
  return MYSTERY_TIERS[type].icon;
}

/**
 * Get Rumio icon
 */
export function getRumioIcon(type: MysteryType): string {
  return RUMIO_CONFIG[type].icon;
}

/**
 * Check if product qualifies for mystery box display
 */
export function shouldShowAsMysteryBox(product: DealProduct): boolean {
  return (
    product.category === 'as_is' &&
    product.is_mystery === true &&
    product.mystery_type !== undefined
  );
}

/**
 * Check if product should have prize highlighting
 */
export function shouldHighlightAsPrize(product: DealProduct): boolean {
  return (
    product.category === 'as_is' &&
    product.is_prize === true &&
    !product.is_mystery
  );
}

/**
 * Get mystery box display info
 */
export function getMysteryBoxInfo(type: MysteryType): {
  title: string;
  description: string;
  icon: string;
  rumioName: string;
  ageRange: string;
} {
  const tierConfig = MYSTERY_TIERS[type];
  const rumioConfig = RUMIO_CONFIG[type];

  return {
    title: `${tierConfig.name} Mystery Box`,
    description: tierConfig.description,
    icon: tierConfig.icon,
    rumioName: rumioConfig.name,
    ageRange: tierConfig.ageRange,
  };
}

/**
 * Get prize highlight display info
 */
export function getPrizeHighlightInfo(type: MysteryType | undefined): {
  badge: string;
  tooltip: string;
} {
  if (!type) {
    return {
      badge: 'Early Access',
      tooltip: 'You have early access to this deal!',
    };
  }

  const tierConfig = MYSTERY_TIERS[type];
  return {
    badge: `${tierConfig.name} Access`,
    tooltip: `You have ${tierConfig.name.toLowerCase()} access (${tierConfig.ageRange}) to this deal!`,
  };
}

/**
 * Get unlock prompt message
 */
export function getUnlockPromptMessage(type: MysteryType, balance: number): string {
  const rumioConfig = RUMIO_CONFIG[type];

  if (balance > 0) {
    return `Use 1 ${rumioConfig.name} to unlock this deal`;
  }

  return `You need a ${rumioConfig.name} to unlock this deal`;
}

/**
 * Get purchase prompt for mystery box
 */
export function getPurchasePrompt(type: MysteryType): string {
  switch (type) {
    case 'super':
      return 'Super Rumios can only be earned through the daily spin or purchased by Pro+ members.';
    case 'fresh':
      return 'Purchase Fresh Rumios to unlock new arrivals before everyone else.';
    case 'standard':
      return 'Purchase Standard Rumios to unlock early access deals.';
  }
}

/**
 * Calculate estimated value of mystery box
 * Based on average discount in the category
 */
export function getEstimatedValue(type: MysteryType): string {
  switch (type) {
    case 'super':
      return 'Highest value - ultra-fresh finds';
    case 'fresh':
      return 'High value - early bird deals';
    case 'standard':
      return 'Good value - before the crowd';
  }
}

/**
 * Get countdown timer text for mystery box
 * Shows when it will become visible without using Rumios
 */
export function getMysteryCountdown(
  firstSeen: string | null,
  userTier: string | null
): string | null {
  if (!firstSeen) return null;

  const ageHours = (Date.now() - new Date(firstSeen).getTime()) / (1000 * 60 * 60);
  const tier = userTier || 'free';

  // Calculate when this will become visible for this tier
  let visibleAt: number;
  if (tier === 'pro+' || tier === 'pro_plus') {
    // Pro+ sees everything except Super (0-3hr) immediately
    if (ageHours < 3) {
      visibleAt = 3 - ageHours;
    } else {
      return null; // Already visible
    }
  } else if (tier === 'pro') {
    // Pro sees Standard (24-48hr) and older
    if (ageHours < 24) {
      visibleAt = 24 - ageHours;
    } else {
      return null; // Already visible
    }
  } else {
    // Free sees 48hr+ only
    if (ageHours < 48) {
      visibleAt = 48 - ageHours;
    } else {
      return null; // Already visible
    }
  }

  // Format the countdown
  if (visibleAt < 1) {
    const minutes = Math.ceil(visibleAt * 60);
    return `Visible in ${minutes}m`;
  } else if (visibleAt < 24) {
    const hours = Math.ceil(visibleAt);
    return `Visible in ${hours}h`;
  } else {
    const days = Math.ceil(visibleAt / 24);
    return `Visible in ${days}d`;
  }
}
